import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../shared/api/client";
import type {
  EnvironmentView,
  ModuleView,
  ReleaseScope,
  ReleaseTemplateSummary,
  ReleaseTemplateVersion,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { RELEASE_TYPE_LABELS } from "../releases/releaseVocabulary";

/**
 * Creating a template (§48).
 *
 * <p>Creates the template and its first draft version together. A template with no version cannot
 * be used for anything, so leaving that as a second step would only give somebody a name they have
 * to remember to come back and finish.
 */
export function TemplateFormDrawer({
  template,
  open,
  onClose,
  onSaved,
}: {
  /** Editing an existing template, or null to create one. */
  template?: ReleaseTemplateSummary | null;
  open: boolean;
  onClose: () => void;
  onSaved: (created: ReleaseTemplateVersion | null) => void;
}) {
  const editing = template != null;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [releaseType, setReleaseType] = useState("");
  const [environmentId, setEnvironmentId] = useState("");
  const [releaseScope, setReleaseScope] = useState<ReleaseScope | "">("");
  const [moduleId, setModuleId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  const environments = useQuery({
    queryKey: ["environments"],
    queryFn: () => api.get<EnvironmentView[]>("/api/v1/environments"),
    enabled: open,
  });

  const modules = useQuery({
    queryKey: ["modules", "offered"],
    queryFn: () => api.get<ModuleView[]>("/api/v1/modules"),
    enabled: open,
  });

  useEffect(() => {
    if (open) {
      setCode(template?.code ?? "");
      setName(template?.name ?? "");
      setDescription(template?.description ?? "");
      setReleaseType(template?.applicableReleaseType ?? "");
      setReleaseScope(template?.applicableReleaseScope ?? "");
      setModuleId(template?.applicableModule?.id ?? "");
      setEnvironmentId(template?.environment?.id ?? "");
      setError(null);
    }
  }, [open, template]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (template) {
        /*
         * The code is not sent. It is how releases already built from this template refer back
         * to it, and renaming what people read is a different act from changing that handle.
         */
        await api.patch(`/api/v1/release-templates/${template.id}`, {
          name: name.trim(),
          description: description.trim() === "" ? null : description.trim(),
          applicableReleaseType: releaseType === "" ? null : releaseType,
          environmentId: environmentId === "" ? null : environmentId,
          applicableReleaseScope: releaseScope === "" ? null : releaseScope,
          applicableModuleId: releaseScope === "" || moduleId === "" ? null : moduleId,
          lockVersion: template.lockVersion,
        });
        await queryClient.invalidateQueries({ queryKey: ["release-templates"] });
        onSaved(null);
        onClose();
        return;
      }

      const created = await api.post<ReleaseTemplateVersion>("/api/v1/release-templates", {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        description: description.trim() === "" ? null : description.trim(),
        applicableReleaseType: releaseType === "" ? null : releaseType,
        environmentId: environmentId === "" ? null : environmentId,
      });
      await queryClient.invalidateQueries({ queryKey: ["release-templates"] });
      onSaved(created);
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : editing
            ? "Could not save the template."
            : "Could not create the template.",
      );
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = code.trim() !== "" && name.trim() !== "";

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? "Edit template" : "New release template"}
      subtitle="The governance a kind of release is held to."
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : editing ? "Save" : "Create template"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <Field
          label="Code"
          required={!editing}
          hint={
            editing
              ? "Fixed once created — releases built from this template refer back to it."
              : "Short and stable — it is how the template is referred to."
          }
        >
          <Input
            autoFocus={!editing}
            value={code}
            disabled={editing}
            placeholder="STD_PROD"
            onChange={(event) => setCode(event.target.value)}
          />
        </Field>

        <Field label="Name" required>
          <Input
            value={name}
            placeholder="Standard Production Release"
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Field label="Description" hint="What this template is for, and when to use it.">
          <Textarea
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>

        <Field
          label="Applies to"
          hint="Offered when creating a release of this kind. Leave blank for any."
        >
          <Select value={releaseType} onChange={(event) => setReleaseType(event.target.value)}>
            <option value="">Any release type</option>
            {Object.entries(RELEASE_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Environment" hint="Optional.">
          <Select
            value={environmentId}
            onChange={(event) => setEnvironmentId(event.target.value)}
          >
            <option value="">Any environment</option>
            {(environments.data ?? []).map((environment) => (
              <option key={environment.id} value={environment.id}>
                {environment.name}
              </option>
            ))}
          </Select>
        </Field>

        {/*
          What kind of release this runbook is for. Worth narrowing: a component release of one
          shared module has the most repeatable runbook there is — the same steps, every customer,
          every time — and it looks nothing like the runbook for a whole-product release.
        */}
        <Field label="Kind of release" hint="Optional. Leave as any and it is offered everywhere.">
          <Select
            value={releaseScope}
            onChange={(event) => {
              setReleaseScope(event.target.value as ReleaseScope | "");
              setModuleId("");
            }}
          >
            <option value="">Any kind of release</option>
            <option value="PROJECT">Whole-product releases</option>
            <option value="MODULE">Module releases</option>
            <option value="COMPONENT">Component releases</option>
          </Select>
        </Field>

        {releaseScope !== "" && releaseScope !== "PROJECT" && (
          <Field
            label="Module"
            hint="Optional. Naming one makes this the runbook for releasing that module, offered ahead of anything more general."
          >
            <Select value={moduleId} onChange={(event) => setModuleId(event.target.value)}>
              <option value="">Any module</option>
              {(modules.data ?? [])
                .filter((module) =>
                  releaseScope === "COMPONENT"
                    ? module.moduleType.sharedVersion
                    : !module.moduleType.sharedVersion)
                .map((module) => (
                  <option key={module.id} value={module.id}>
                    {module.name}
                  </option>
                ))}
            </Select>
          </Field>
        )}

        <p className="rounded-md bg-ink-50 px-3 py-2 text-xs text-ink-600">
          A first draft version is created with it. Add the prerequisites, approvals, runbook and
          checks to that draft, then activate it — from that point it is what new releases are
          built from, and it can no longer be edited.
        </p>
      </div>
    </Drawer>
  );
}
